"use client"
import { Button, SkeletonCircle, SkeletonText, Skeleton, Image } from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import nodata from "./nodata1.png"
import { Link } from "react-router-dom"
import {
  faBriefcase,
  faBuilding,
  faEye,
  faIndianRupee,
  faMapMarkerAlt,
  faUserGraduate,
  faClock,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  fetchRecommendedJobsAsync,
  selectRecommendedJobs,
} from "../../HomeAfterLogin.js/RecommendedJobsSlice/RecommendedJobsSlice"
import { TimeDifference } from "../../common/TimeDifference"
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs"

function RecommendedJobs() {
  const dispatch = useDispatch()
  const jobs = useSelector(selectRecommendedJobs)
  const [isLoading, setIsLoading] = useState(true)
  const [savedJobs, setSavedJobs] = useState(new Set())
  const [bookmarkingJobs, setBookmarkingJobs] = useState(new Set())
  const initialRender = useRef(true)
  useEffect(() => {
    // Only run on initial render
    if (initialRender.current) {
      initialRender.current = false

      // Only fetch if we don't have jobs data
      if (!jobs || jobs.length === 0) {
        dispatch(fetchRecommendedJobsAsync())
          .then(() => setIsLoading(false))
          .catch(() => setIsLoading(false))
      } else {
        setIsLoading(false)
      }
    }

    window.scrollTo(0, 0)
  }, [dispatch, jobs])

  const handleToggleBookmark = (jobId, e) => {
    e.stopPropagation()
    setBookmarkingJobs((prev) => new Set(prev).add(jobId))
    setTimeout(() => {
      setSavedJobs((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(jobId)) {
          newSet.delete(jobId)
        } else {
          newSet.add(jobId)
        }
        return newSet
      })
      setBookmarkingJobs((prev) => {
        const newSet = new Set(prev)
        newSet.delete(jobId)
        return newSet
      })
    }, 1000)
  }

  const isJobSaved = (jobId) => savedJobs.has(jobId)
  const isJobBeingBookmarked = (jobId) => bookmarkingJobs.has(jobId)

  const handleJobClick = (job) => {
    // Handle job click - could navigate to job details
    console.log("Job clicked:", job)
  }

  const renderSkeletonCards = () => {
    return [...Array(6)].map((_, index) => (
      <div key={`skeleton-${index}`} className="detailed-card loading">
        <div className="card-header-section">
          <div className="avatar-info">
            <div className="avatar-container">
              <SkeletonCircle size="50px" />
            </div>
            <div className="basic-info">
              <Skeleton height="18px" width="140px" mb={2} />
              <Skeleton height="13px" width="180px" mb={2} />
              <div className="flex gap-2">
                <Skeleton height="16px" width="60px" borderRadius="10px" />
                <Skeleton height="16px" width="80px" borderRadius="10px" />
              </div>
            </div>
          </div>
          <div className="quick-stats">
            <Skeleton height="28px" width="60px" borderRadius="8px" />
          </div>
        </div>
        <div className="card-details">
          <div className="detail-row">
            <Skeleton height="13px" width="100%" mb={3} />
            <Skeleton height="13px" width="85%" mb={3} />
          </div>
          <div className="detail-row">
            <Skeleton height="13px" width="90%" mb={3} />
            <Skeleton height="13px" width="75%" mb={3} />
          </div>
          <SkeletonText mt={4} noOfLines={2} spacing={2} />
        </div>
        <div className="card-actions">
          <SkeletonCircle size="36px" />
          <SkeletonCircle size="36px" />
          <SkeletonCircle size="36px" />
          <SkeletonCircle size="36px" />
        </div>
      </div>
    ))
  }

  const renderJobCards = () => {
    return jobs.map((job) => (
      <div key={job.id} className="detailed-card" onClick={() => handleJobClick(job)}>
        {/* Header Section */}
        <div className="card-header-section">
          <div className="avatar-info">
            <div className="avatar-container">
              {job.company_logo ? (
                <Image
                  className="card-avatar border-1"
                  src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}`}
                  alt={job.company_name}
                />
              ) : (
                <div className="card-avatar bg-blue-100 flex items-center justify-center rounded-full border">
                  <FontAwesomeIcon icon={faBriefcase} className="text-blue-500 text-lg" />

                </div>
              )}
            </div>
            <div className="basic-info">
              <h3 className="candidate-name-detailed hover:underline mb-2">
                {job?.job_title?.title || job?.job_name || "Title Not Available"}
              </h3>
              <div className="candidate-email-detailed">{job.company_name}</div>
              <div className="status-badges">
                <span className="unlock-badge unlocked">{job?.job_tag}</span>
                <span className="match-badge">Recommended</span>
              </div>
            </div>
          </div>
        </div>
        {/* Details Section */}
        <div className="card-details">
          <div className="detail-row">
            <div className="detail-item">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon" />

              <span className="detail-text">
                {job.job_locations?.map((loc) => loc.job_location?.title).join(", ") || "Location not specified"}
              </span>
            </div>
            <div className="detail-item">
              <FontAwesomeIcon icon={faUserGraduate} className="detail-icon" />
              <span className="detail-text">{job?.job_type}</span>
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-item">
              <FontAwesomeIcon icon={faIndianRupee} className="detail-icon" />
              <span className="detail-text">
                {job.salary_min_amount === null || job?.salary_min_amount === 0
                  ? "Not Disclosed"
                  : `₹${job?.salary_min_amount || "N/A"} - ₹${job?.salary_max_amount || "N/A"} / ${job?.salary_per_type || "Not Specified"}`}
              </span>
            </div>
            <div className="detail-item">
              <FontAwesomeIcon icon={faBuilding} className="detail-icon" />
              <span className="detail-text">{job.company_name}</span>
            </div>
          </div>
          {job.job_description && (
            <div className="skills-section">
              <div className="skills-label">
                <FontAwesomeIcon icon={faBriefcase} className="detail-icon" />
                <span>Description:</span>
              </div>
              <div className="skills-list">
                {job.job_description.length > 100 ? `${job.job_description.substring(0, 100)}...` : job.job_description}
              </div>
            </div>
          )}
          <div className="job-section">
            <div className="job-info">
              <FontAwesomeIcon icon={faEye} className="detail-icon" />
              <Link to={`/jobs/${job?.uuid}`} className="job-link hover:underline">
                View Job Details
              </Link>
            </div>
          </div>
          <div className="time-info">
            <FontAwesomeIcon icon={faClock} className="detail-icon" />
            <TimeDifference dateTimeString={job.created_at} />
          </div>
        </div>
        {/* Actions Section */}
      </div>
    ))
  }

  return (
    <div className="">
      <div style={{ fontFamily: "Inter, sans-serif" }}>
        <div className="py-8 min-h-screen bg-mainBg sm:px-6 sm:py-4">
          <div className="px-4 sm:px-0.5">
            <h1 className="text-3xl font-bold text-left text-gray-800 mb-8">Recommended Jobs</h1>
            <div className="w-full bg-white rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-6">My Job Recommendations</h2>
              {isLoading ? (
                <div className="detailed-grid">{renderSkeletonCards()}</div>
              ) : jobs && jobs.length > 0 ? (
                <div className="detailed-grid">{renderJobCards()}</div>
              ) : (
                <div className="no-candidates">
                  <div className="text-center">
                    <img
                      src={nodata || "/placeholder.svg"}
                      alt="No data available"
                      className="w-48 h-auto mb-4 mx-auto"
                    />
                    <p className="no-candidates-text mb-4">No Recommended Jobs yet!</p>
                    <Button colorScheme="blue" onClick={() => (window.location.href = "/jobs")}>
                      Explore Jobs
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="text-center text-gray-600 mt-8 text-sm">
              <p>© {new Date().getFullYear()} IndiaHirings.com All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
      {/* Styles from your card component */}
      <style jsx>{`
/* ==== Layout ==== */
/* Card grid layout */
.detailed-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 16px;
}

/* ==== Basic Theme Colors ==== */
.detailed-card {
  --brand: #2563eb; /* your main color */
  --bg-card: #ffffff;
  --bg-soft: #f8fafc;
  --bg-subtle: #f1f5f9;
  --text-strong: #0f172a;
  --text-default: #334155;
  --text-muted: #64748b;
  --border: #e6eaf2;
  --border-strong: #d7deea;
  --shadow-sm: 0 1px 2px rgba(2, 6, 23, 0.05);
  --shadow-md: 0 8px 20px rgba(2, 6, 23, 0.06);
  --shadow-lg: 0 16px 32px rgba(2, 6, 23, 0.08);
}

/* ==== Card Style ==== */
.detailed-card {
  position: relative;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s, box-shadow 0.25s, border-color 0.2s;
  cursor: pointer;
  overflow: hidden;
}

/* Small left color stripe */
.detailed-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: var(--brand);
  opacity: 0.18;
  transition: opacity 0.2s;
}

/* Hover effect */
.detailed-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--border-strong);
}
.detailed-card:hover::before {
  opacity: 0.38;
}

/* Focus effect */
.detailed-card:focus-within {
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12), var(--shadow-md);
  border-color: #cfe1ff;
  outline: none;
}

/* ==== Header Section ==== */
.card-header-section {
  padding: 18px 20px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid var(--bg-subtle);
  background: linear-gradient(180deg, #fff, #fcfdff);
}

/* Avatar + Info area */
.avatar-info {
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 12px;
  align-items: center;
  flex: 1;
}

.avatar-container {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: var(--bg-soft);
  padding: 2px;
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.card-avatar {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  object-fit: cover;
  background: #eff6ff;
  box-shadow: 0 1px 2px rgba(2, 6, 23, 0.06);
}

.candidate-name-detailed {
  font-size: 17px;
  font-weight: 800;
  color: var(--text-strong);
  margin-bottom: 4px;
  text-transform: capitalize;
}
.candidate-name-detailed:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.candidate-email-detailed {
  font-size: 13px;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ==== Badges ==== */
.status-badges {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.unlock-badge,
.match-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid transparent;
}

.unlock-badge.unlocked {
  background: #ecfdf5;
  border-color: #bbf7d0;
  color: #065f46;
}

.match-badge {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

/* ==== Details Section ==== */
.card-details {
  padding: 14px 20px 16px;
  background: #fff;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  padding: 8px 10px;
  background: var(--bg-soft);
  border: 1px solid #eef2f7;
  border-radius: 10px;
}

.detail-icon {
  font-size: 13px;
  color: #64748b;
}

.detail-text {
  font-size: 13px;
  color: var(--text-default);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ==== Skills Section ==== */
.skills-section {
  margin: 10px 0 12px;
  background: linear-gradient(180deg, #f8fafc, #f1f5f9);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
}

.skills-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 4px;
}

.skills-list {
  font-size: 12.5px;
  color: #5b6779;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ==== Job Section ==== */
.job-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 8px 0 12px;
}

.job-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  background: #f0f9ff;
  border: 1px solid #dbeafe;
  padding: 8px 10px;
  border-radius: 10px;
  transition: background 0.2s, border-color 0.2s;
}

.job-info:hover {
  background: #e8f3ff;
  border-color: #c7ddff;
}

.job-link {
  font-size: 13px;
  color: var(--brand);
  font-weight: 700;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.job-link:hover {
  color: #1e40af;
  text-decoration: underline;
}

.time-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 600;
}

/* ==== Action Buttons ==== */
.card-actions {
  display: flex;
  justify-content: space-evenly;
  padding: 12px 20px 16px;
  border-top: 1px solid var(--bg-subtle);
  background: linear-gradient(180deg, #fff, #fafbfc);
}

.action-btn-detailed {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s;
}

.action-btn-detailed:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(2, 6, 23, 0.08);
  background: #f8fafc;
}

.btn-green { border-color: #10b981; color: #10b981; }
.btn-red { border-color: #ef4444; color: #ef4444; }
.btn-yellow { border-color: #f59e0b; color: #f59e0b; }
.btn-blue { border-color: var(--brand); color: var(--brand); }
.btn-gray { border-color: #9ca3af; color: #6b7280; }

/* ==== Miscellaneous ==== */
.no-candidates {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.08);
  padding: 32px;
  text-align: center;
  border: 1px solid #e5e7eb;
}
.no-candidates-text {
  color: #6b7280;
  font-size: 18px;
}

/* ==== Responsive ==== */
@media (max-width: 768px) {
  .detailed-grid { grid-template-columns: 1fr; gap: 12px; }
  .card-header-section { flex-direction: column; gap: 12px; }
  .detail-row { flex-direction: column; gap: 8px; }
  .job-section { flex-direction: column; align-items: flex-start; gap: 8px; }
}

/* ==== Reduced Motion ==== */
@media (prefers-reduced-motion: reduce) {
  .detailed-card, .action-btn-detailed, .job-info {
    transition: none !important;
  }
}
`}</style>

    </div>
  )
}
export default RecommendedJobs
